import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { JSONEditorFormItem } from '@/components/ui/form-fields/JsonEditorFormItem';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Separator } from '@/components/ui/Separator';
import { Switch } from '@/components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import TestNotificationButtons from '@/features/Customers/components/TestNotificationButtons';
import {
  createDefaultCustomerSecretDrafts,
  CustomerSecretDrafts,
  CustomerSecretInput,
  CustomerSecretTarget,
  CustomerSecretTargetId,
  customerSecretTargets,
  getCustomerSecretStatusText,
} from '@/features/Customers/customerSecretStore';
import { cn } from '@/lib/utils';
import { CustomerConfig } from '@/models/customer.models';
import { JSONConfig } from '@/models/device.models';
import { ChevronRight, Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Control,
  Controller,
  UseFormRegister,
  UseFormSetError,
  useFormState,
  useWatch,
} from 'react-hook-form';

export type CustomerFormValues = {
  name: string;
  storagePath: string;
  config: CustomerConfig;
  jsonConfig: JSONConfig;
};

export type CustomerFormMode = 'form' | 'json';

export type CustomerDetailsFormProps<_TFormValues extends CustomerFormValues> =
  {
    register: UseFormRegister<CustomerFormValues>;
    control: Control<CustomerFormValues>;
    setError: UseFormSetError<CustomerFormValues>;
    readOnly?: boolean;
    className?: string;
    creatingCustomer?: boolean;
    // Set when editing a saved customer; enables the "send test" buttons.
    customerId?: string;
    formMode: CustomerFormMode;
    setFormMode: (mode: CustomerFormMode) => void;
    secretDrafts?: CustomerSecretDrafts;
    onStageSecretUpsert?: (
      targetId: CustomerSecretTargetId,
      secret: CustomerSecretInput,
    ) => void;
    onStageSecretDelete?: (targetId: CustomerSecretTargetId) => void;
  };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const tryJsonParseString = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'string' ? parsed : value;
  } catch {
    return value;
  }
};

// A product's entry in SystemPromptByProduct, matched case-insensitively as
// senhub does; unset when there is none, so a save adds no key.
const productPrompt = (
  byProduct: unknown,
  product: string,
): string | undefined => {
  if (!isRecord(byProduct)) return undefined;
  const entry = Object.entries(byProduct).find(
    ([key]) => key.toLowerCase() === product.toLowerCase(),
  );
  return typeof entry?.[1] === 'string' && entry[1] !== ''
    ? entry[1]
    : undefined;
};

const normalizeExternalServiceConfig = (
  value: unknown,
): { Url: string; ApiUsernamePasswordSecret: string } => {
  if (!isRecord(value)) {
    return {
      Url: '',
      ApiUsernamePasswordSecret: '',
    };
  }

  return {
    Url: typeof value.Url === 'string' ? value.Url : '',
    ApiUsernamePasswordSecret:
      typeof value.ApiUsernamePasswordSecret === 'string'
        ? value.ApiUsernamePasswordSecret
        : '',
  };
};

const normalizeFtpConfig = (
  value: unknown,
): { Server: string; Port: string } => {
  if (!isRecord(value)) {
    return {
      Server: '',
      Port: '',
    };
  }

  return {
    Server: typeof value.Server === 'string' ? value.Server : '',
    Port: typeof value.Port === 'string' ? value.Port : '',
  };
};

export const normalizeCustomerConfig = (config: unknown): CustomerConfig => {
  if (!isRecord(config)) {
    return {};
  }

  const senpicRecord = isRecord(config.Senpic) ? config.Senpic : {};
  const legacySenbos = normalizeExternalServiceConfig(config.Senbos);
  const legacyStagingSenbos = normalizeExternalServiceConfig(
    config.StagingSenbos,
  );

  const senpicSenbos = normalizeExternalServiceConfig(senpicRecord.Senbos);
  const senpicStagingSenbos = normalizeExternalServiceConfig(
    senpicRecord.StagingSenbos,
  );

  const normalizeHasura = (v: unknown) => {
    const r = isRecord(v) ? v : {};
    return {
      Url: typeof r.Url === 'string' ? r.Url : '',
      ApiKeyId: typeof r.ApiKeyId === 'number' ? r.ApiKeyId : 0,
    };
  };

  const normalizeFailureNotification = (v: unknown) => {
    const r = isRecord(v) ? v : {};
    return {
      // Left unset (not 10) when absent, so a save does not pin senhub's default.
      Threshold: typeof r.Threshold === 'number' ? r.Threshold : undefined,
      SlackWebhookUrl:
        typeof r.SlackWebhookUrl === 'string' ? r.SlackWebhookUrl : '',
      EmailRecipients: Array.isArray(r.EmailRecipients)
        ? r.EmailRecipients.filter((e): e is string => typeof e === 'string')
        : [],
      SmtpCredentialsSecret:
        typeof r.SmtpCredentialsSecret === 'string'
          ? r.SmtpCredentialsSecret
          : '',
    };
  };

  const normalizeLlm = (v: unknown) => {
    const r = isRecord(v) ? v : {};
    return {
      Model: typeof r.Model === 'string' ? r.Model : '',
      ApiKey: typeof r.ApiKey === 'string' ? r.ApiKey : '',
      ApiUrl: typeof r.ApiUrl === 'string' ? r.ApiUrl : '',
      MaxTokens: typeof r.MaxTokens === 'number' ? r.MaxTokens : 2048,
      // Left unset (not 180) when absent, so a save does not pin senhub's default.
      TimeoutSeconds:
        typeof r.TimeoutSeconds === 'number' ? r.TimeoutSeconds : undefined,
      SystemPrompt: tryJsonParseString(r.SystemPrompt),
      SystemPromptByProduct: {
        Senpic: productPrompt(r.SystemPromptByProduct, 'Senpic'),
        Senforce: productPrompt(r.SystemPromptByProduct, 'Senforce'),
      },
    };
  };

  return {
    Ftp: normalizeFtpConfig(config.Ftp),

    Grafana: {
      DashboardUid:
        isRecord(config.Grafana) &&
        typeof config.Grafana.DashboardUid === 'string'
          ? config.Grafana.DashboardUid
          : '',
      DashboardSlug:
        isRecord(config.Grafana) &&
        typeof config.Grafana.DashboardSlug === 'string'
          ? config.Grafana.DashboardSlug
          : '',
      OrganizationId:
        isRecord(config.Grafana) &&
        typeof config.Grafana.OrganizationId === 'string'
          ? config.Grafana.OrganizationId
          : '',
    },
    Senpic: {
      Senbos: {
        Url: senpicSenbos.Url || legacySenbos.Url,
        ApiUsernamePasswordSecret:
          senpicSenbos.ApiUsernamePasswordSecret ||
          legacySenbos.ApiUsernamePasswordSecret,
      },
      StagingSenbos: {
        Url: senpicStagingSenbos.Url || legacyStagingSenbos.Url,
        ApiUsernamePasswordSecret:
          senpicStagingSenbos.ApiUsernamePasswordSecret ||
          legacyStagingSenbos.ApiUsernamePasswordSecret,
      },
    },
    Senshield: {
      Senbos: normalizeExternalServiceConfig(
        isRecord(config.Senshield) ? config.Senshield.Senbos : undefined,
      ),
    },
    Senforce: {
      Senbos: normalizeExternalServiceConfig(
        isRecord(config.Senforce) ? config.Senforce.Senbos : undefined,
      ),
    },
    AlertReviewer: {
      // Left unset (not true) when absent, so a save does not pin senhub's default.
      Enabled:
        isRecord(config.AlertReviewer) &&
        typeof config.AlertReviewer.Enabled === 'boolean'
          ? config.AlertReviewer.Enabled
          : undefined,
      // Left unset (not false) when absent, so a save does not pin senhub's default.
      UpdateAlertStatusEnabled:
        isRecord(config.AlertReviewer) &&
        typeof config.AlertReviewer.UpdateAlertStatusEnabled === 'boolean'
          ? config.AlertReviewer.UpdateAlertStatusEnabled
          : undefined,
      Provider:
        isRecord(config.AlertReviewer) &&
        typeof config.AlertReviewer.Provider === 'string'
          ? config.AlertReviewer.Provider
          : '',
      Senbos: normalizeExternalServiceConfig(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Senbos
          : undefined,
      ),
      Gpt: normalizeLlm(
        isRecord(config.AlertReviewer) ? config.AlertReviewer.Gpt : undefined,
      ),
      Claude: normalizeLlm(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Claude
          : undefined,
      ),
      Gemini: normalizeLlm(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Gemini
          : undefined,
      ),
      Hasura: normalizeHasura(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Hasura
          : undefined,
      ),
      FrameTypes:
        isRecord(config.AlertReviewer) &&
        Array.isArray(config.AlertReviewer.FrameTypes) &&
        (config.AlertReviewer.FrameTypes as string[]).length > 0
          ? (config.AlertReviewer.FrameTypes as string[])
          : undefined,
      FailureNotification: normalizeFailureNotification(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.FailureNotification
          : undefined,
      ),
    },
  };
};

const parseCommaList = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const CommaListInput = ({
  id,
  value,
  readOnly,
  placeholder,
  onCommit,
}: {
  id: string;
  value: string[] | undefined;
  readOnly: boolean;
  placeholder: string;
  onCommit: (text: string) => void;
}) => {
  const [text, setText] = useState(value?.join(', ') ?? '');
  return (
    <Input
      type="text"
      id={id}
      readOnly={readOnly}
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onCommit(text)}
    />
  );
};

const FrameTypesInput = ({
  value,
  readOnly,
  onChange,
}: {
  value: string[] | undefined;
  readOnly: boolean;
  onChange: (value: string[] | undefined) => void;
}) => (
  <CommaListInput
    id="alert-reviewer-frame-types"
    value={value}
    readOnly={readOnly}
    placeholder="Leave empty to send all frame types"
    onCommit={(text) => {
      const frameTypes = parseCommaList(text).map((s) => s.toUpperCase());
      onChange(frameTypes.length > 0 ? frameTypes : undefined);
    }}
  />
);

type AlertReviewerProvider = 'Gpt' | 'Claude' | 'Gemini';

// The products senhub tells apart per alert (from GPS) to pick
// SystemPromptByProduct; keyed as senhub spells them.
const ALERT_REVIEW_PRODUCTS = [
  {
    key: 'Senpic',
    label: 'SenPIC Prompt',
    hint: 'Pole cameras.',
  },
  {
    key: 'Senforce',
    label: 'SenFORCE Prompt',
    hint: 'Vehicle-mounted cameras.',
  },
] as const;

type PromptFieldName =
  | `config.AlertReviewer.${AlertReviewerProvider}.SystemPrompt`
  | `config.AlertReviewer.${AlertReviewerProvider}.SystemPromptByProduct.${(typeof ALERT_REVIEW_PRODUCTS)[number]['key']}`;

const PromptField = ({
  name,
  inputId,
  label,
  hint,
  emptyText,
  unsetWhenEmpty = false,
  register,
  control,
  readOnly,
}: {
  name: PromptFieldName;
  inputId: string;
  label: string;
  hint?: string;
  emptyText: string;
  // Store an empty prompt as no key at all, so senhub falls back to another.
  unsetWhenEmpty?: boolean;
  register: UseFormRegister<CustomerFormValues>;
  control: Control<CustomerFormValues>;
  readOnly: boolean;
}) => {
  // Watched here, not in the form, so typing a prompt re-renders only this field.
  const prompt = useWatch({ control, name });
  const lineCount = prompt ? prompt.split('\n').length : 0;
  // Starts collapsed so long prompts don't bury the provider cards; the line
  // count shows whether there is one.
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="grid gap-2">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex w-fit items-center gap-1 text-left"
          aria-controls={inputId}
        >
          <ChevronRight className="size-4 transition-transform group-data-[state=open]:rotate-90" />
          <Label asChild>
            <span>{label}</span>
          </Label>
          {!open && (
            <span className="text-muted-foreground text-xs">
              {lineCount > 0
                ? `(${lineCount} ${lineCount === 1 ? 'line' : 'lines'})`
                : `(${emptyText})`}
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      {/* Kept mounted while closed so the textarea stays registered. */}
      <CollapsibleContent
        forceMount
        className="grid gap-1 data-[state=closed]:hidden"
      >
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        <Textarea
          id={inputId}
          readOnly={readOnly}
          rows={4}
          className="font-mono text-xs"
          {...register(
            name,
            unsetWhenEmpty
              ? {
                  setValueAs: (v: unknown) =>
                    typeof v === 'string' && v.trim() !== '' ? v : undefined,
                }
              : undefined,
          )}
        />
      </CollapsibleContent>
    </Collapsible>
  );
};

const ProviderPromptFields = ({
  provider,
  register,
  control,
  readOnly,
}: {
  provider: AlertReviewerProvider;
  register: UseFormRegister<CustomerFormValues>;
  control: Control<CustomerFormValues>;
  readOnly: boolean;
}) => {
  const idPrefix = `alert-reviewer-${provider.toLowerCase()}`;

  return (
    <div className="grid gap-3">
      <PromptField
        name={`config.AlertReviewer.${provider}.SystemPrompt`}
        inputId={`${idPrefix}-system-prompt`}
        label="System Prompt"
        hint="Used for every alert, unless its product has a prompt below."
        emptyText="empty"
        register={register}
        control={control}
        readOnly={readOnly}
      />
      {ALERT_REVIEW_PRODUCTS.map((product) => (
        <PromptField
          key={product.key}
          name={`config.AlertReviewer.${provider}.SystemPromptByProduct.${product.key}`}
          inputId={`${idPrefix}-${product.key.toLowerCase()}-prompt`}
          label={product.label}
          hint={`${product.hint} Replaces the System Prompt for these alerts; leave empty to use it.`}
          emptyText="uses System Prompt"
          unsetWhenEmpty
          register={register}
          control={control}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

const SLACK_WEBHOOK_PREFIX = 'https://hooks.slack.com/';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// senhub caps one review's AI calls, retries included, at 10 minutes.
const MAX_AI_TIMEOUT_SECONDS = 600;
// senhub sends to at most this many recipients.
const MAX_EMAIL_RECIPIENTS = 20;

const CustomerDetailsForm = <_TFormValues extends CustomerFormValues>({
  register,
  control,
  setError,
  readOnly = false,
  creatingCustomer = false,
  className,
  formMode,
  setFormMode,
  secretDrafts = createDefaultCustomerSecretDrafts(),
  onStageSecretUpsert,
  onStageSecretDelete,
  customerId,
}: CustomerDetailsFormProps<_TFormValues>) => {
  const [secretEditorTarget, setSecretEditorTarget] =
    useState<CustomerSecretTarget | null>(null);
  const [secretEditorUsername, setSecretEditorUsername] = useState('');
  const [secretEditorPassword, setSecretEditorPassword] = useState('');
  const [secretEditorError, setSecretEditorError] = useState('');
  const senpicSenbosSecret = useWatch({
    control,
    name: 'config.Senpic.Senbos.ApiUsernamePasswordSecret',
  });
  const senpicStagingSenbosSecret = useWatch({
    control,
    name: 'config.Senpic.StagingSenbos.ApiUsernamePasswordSecret',
  });
  const senshieldSenbosSecret = useWatch({
    control,
    name: 'config.Senshield.Senbos.ApiUsernamePasswordSecret',
  });
  const senforceSenbosSecret = useWatch({
    control,
    name: 'config.Senforce.Senbos.ApiUsernamePasswordSecret',
  });
  const alertReviewerSenbosSecret = useWatch({
    control,
    name: 'config.AlertReviewer.Senbos.ApiUsernamePasswordSecret',
  });
  const alertReviewerGptApiKey = useWatch({
    control,
    name: 'config.AlertReviewer.Gpt.ApiKey',
  });
  const alertReviewerClaudeApiKey = useWatch({
    control,
    name: 'config.AlertReviewer.Claude.ApiKey',
  });
  const alertReviewerGeminiApiKey = useWatch({
    control,
    name: 'config.AlertReviewer.Gemini.ApiKey',
  });
  const { errors } = useFormState({ control });
  const failureThresholdError =
    errors.config?.AlertReviewer?.FailureNotification?.Threshold?.message;
  const alertReviewerSlackWebhook = useWatch({
    control,
    name: 'config.AlertReviewer.FailureNotification.SlackWebhookUrl',
  });
  const alertReviewerSmtp = useWatch({
    control,
    name: 'config.AlertReviewer.FailureNotification.SmtpCredentialsSecret',
  });
  const alertReviewerEmailRecipients = useWatch({
    control,
    name: 'config.AlertReviewer.FailureNotification.EmailRecipients',
  });

  const handleConfigViewChange = (value: string) => {
    const nextView = value === 'json' ? 'json' : 'form';
    setFormMode?.(nextView);
  };

  const activeSecretDraft = useMemo(() => {
    if (!secretEditorTarget) {
      return null;
    }

    return secretDrafts[secretEditorTarget.id];
  }, [secretDrafts, secretEditorTarget]);

  const openSecretEditor = (target: CustomerSecretTarget) => {
    setSecretEditorTarget(target);
    setSecretEditorError('');

    const draft = secretDrafts[target.id];
    if (draft.action === 'upsert') {
      setSecretEditorUsername(draft.username);
      setSecretEditorPassword(draft.password);
      return;
    }

    setSecretEditorUsername('');
    setSecretEditorPassword('');
  };

  const closeSecretEditor = () => {
    setSecretEditorTarget(null);
    setSecretEditorUsername('');
    setSecretEditorPassword('');
    setSecretEditorError('');
  };

  const handleSecretSave = () => {
    if (!secretEditorTarget || !onStageSecretUpsert) {
      closeSecretEditor();
      return;
    }

    const { secretKind } = secretEditorTarget;

    if (secretKind === 'apiKey') {
      const apiKey = secretEditorPassword.trim();
      if (!apiKey) {
        setSecretEditorError('API Key is required.');
        return;
      }
      onStageSecretUpsert(secretEditorTarget.id, { apiKey });
    } else if (secretKind === 'url') {
      const url = secretEditorPassword.trim();
      // senhub refuses to post anywhere else.
      if (!url.toLowerCase().startsWith(SLACK_WEBHOOK_PREFIX)) {
        setSecretEditorError(`URL must start with ${SLACK_WEBHOOK_PREFIX}`);
        return;
      }
      onStageSecretUpsert(secretEditorTarget.id, { url });
    } else {
      const username = secretEditorUsername.trim();
      const password = secretEditorPassword.trim();

      if (!username) {
        setSecretEditorError('Username is required.');
        return;
      }

      if (!password) {
        setSecretEditorError('Password is required.');
        return;
      }

      onStageSecretUpsert(secretEditorTarget.id, { username, password });
    }

    closeSecretEditor();
  };

  const handleSecretDelete = () => {
    if (!secretEditorTarget || !onStageSecretDelete) {
      closeSecretEditor();
      return;
    }

    onStageSecretDelete(secretEditorTarget.id);
    closeSecretEditor();
  };

  const renderSecretField = (
    target: CustomerSecretTarget,
    inputId: string,
    label: string,
  ) => {
    const draft = secretDrafts[target.id];
    const statusText = getCustomerSecretStatusText(draft);
    const currentValue = getExistingSecretValue(target.id);
    const displayValue = draft.action === 'none' ? currentValue : statusText;
    const isStatusValue = draft.action !== 'none';

    return (
      <div className="grid gap-2">
        <Label htmlFor={inputId}>{label}</Label>
        <div className="flex gap-2">
          <Input
            type="text"
            id={inputId}
            readOnly
            value={displayValue}
            className={isStatusValue ? 'text-muted-foreground' : undefined}
          />
          {!readOnly && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => openSecretEditor(target)}
              aria-label={`Edit ${label}`}
              title={`Edit ${label}`}
            >
              <Pencil />
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderTimeoutField = (provider: 'Gpt' | 'Claude' | 'Gemini') => {
    const inputId = `alert-reviewer-${provider.toLowerCase()}-timeout`;
    const error =
      errors.config?.AlertReviewer?.[provider]?.TimeoutSeconds?.message;

    return (
      <div className="grid gap-2">
        <Label htmlFor={inputId}>Timeout (seconds)</Label>
        <Input
          type="number"
          id={inputId}
          min={1}
          max={MAX_AI_TIMEOUT_SECONDS}
          step={1}
          readOnly={readOnly}
          placeholder="180 (default)"
          {...register(`config.AlertReviewer.${provider}.TimeoutSeconds`, {
            // Empty = unset, so senhub's default applies.
            setValueAs: (v: unknown) =>
              v === '' || v === null || v === undefined ? undefined : Number(v),
            validate: (v) =>
              v === undefined ||
              (Number.isInteger(v) && v >= 1 && v <= MAX_AI_TIMEOUT_SECONDS) ||
              `Timeout must be a whole number from 1 to ${MAX_AI_TIMEOUT_SECONDS}.`,
          })}
        />
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    );
  };

  const getSecretTarget = (
    targetId: CustomerSecretTargetId,
  ): CustomerSecretTarget =>
    customerSecretTargets.find((t) => t.id === targetId)!;

  const getHasExistingSecret = (targetId: CustomerSecretTargetId): boolean => {
    const value = getExistingSecretValue(targetId);

    return value.trim().length > 0;
  };

  const existingSecretValues: Record<CustomerSecretTargetId, unknown> = {
    senpicSenbos: senpicSenbosSecret,
    senpicStagingSenbos: senpicStagingSenbosSecret,
    senshieldSenbos: senshieldSenbosSecret,
    senforceSenbos: senforceSenbosSecret,
    alertReviewerSenbos: alertReviewerSenbosSecret,
    alertReviewerGptApiKey: alertReviewerGptApiKey,
    alertReviewerClaudeApiKey: alertReviewerClaudeApiKey,
    alertReviewerGeminiApiKey: alertReviewerGeminiApiKey,
    alertReviewerSlackWebhook: alertReviewerSlackWebhook,
    alertReviewerSmtp: alertReviewerSmtp,
  };

  const getExistingSecretValue = (targetId: CustomerSecretTargetId): string => {
    const value = existingSecretValues[targetId];

    return typeof value === 'string' ? value : '';
  };

  const canDeleteSecret = Boolean(
    secretEditorTarget &&
      (getHasExistingSecret(secretEditorTarget.id) ||
        activeSecretDraft?.action === 'upsert' ||
        activeSecretDraft?.action === 'delete'),
  );

  return (
    <div className={cn('grid gap-6', className)}>
      <div className="grid gap-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          type="text"
          id="name"
          readOnly={readOnly}
          {...register('name', {
            required: 'Name is required',
          })}
        />
      </div>
      {!creatingCustomer && (
        <div className="grid gap-2">
          <Label htmlFor="storagePath">Storage Path *</Label>
          <Input
            type="text"
            id="storagePath"
            readOnly={readOnly}
            {...register('storagePath', {
              required: 'Storage Path is required',
            })}
          />
        </div>
      )}
      <Tabs
        value={formMode}
        onValueChange={handleConfigViewChange}
        className="grid rounded-xl"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger disabled={!creatingCustomer && !readOnly} value="form">
            Form
          </TabsTrigger>
          <TabsTrigger value="json" disabled={!creatingCustomer && !readOnly}>
            JSON Form
          </TabsTrigger>
        </TabsList>
        <Card>
          <CardContent className="grid gap-4">
            <TabsContent value="form" className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="ftp-port">FTP Port</Label>
                  <Input
                    type="number"
                    id="ftp-port"
                    readOnly={readOnly}
                    {...register('config.Ftp.Port')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ftp-server">FTP Server</Label>
                  <Input
                    type="text"
                    id="ftp-server"
                    readOnly={readOnly}
                    {...register('config.Ftp.Server')}
                  />
                </div>
              </div>

              <Separator orientation="horizontal" />

              <div className="grid gap-4 md:grid-cols-3">
                <div className="grid gap-2">
                  <Label htmlFor="grafana-dashboard-uid"> Grafana UID</Label>
                  <Input
                    type="text"
                    id="grafana-dashboard-uid"
                    readOnly={readOnly}
                    {...register('config.Grafana.DashboardUid')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="grafana-dashboard-slug">
                    Grafana Dashboard Slug
                  </Label>
                  <Input
                    type="text"
                    id="grafana-dashboard-slug"
                    readOnly={readOnly}
                    {...register('config.Grafana.DashboardSlug')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="grafana-organization-id">
                    Grafana Organization ID
                  </Label>
                  <Input
                    type="text"
                    id="grafana-organization-id"
                    readOnly={readOnly}
                    {...register('config.Grafana.OrganizationId')}
                  />
                </div>
              </div>

              <Separator orientation="horizontal" />

              <Tabs defaultValue="senpic" className="grid rounded-xl">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="senpic">Senpic</TabsTrigger>
                  <TabsTrigger value="senshield">Senshield</TabsTrigger>
                  <TabsTrigger value="senforce">Senforce</TabsTrigger>
                  <TabsTrigger value="alert-reviewer">
                    Alert Reviewer
                  </TabsTrigger>
                </TabsList>
                <Card>
                  <CardContent>
                    <TabsContent value="senpic">
                      <div className="flex flex-col gap-4 pt-2">
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senpic.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senpicSenbos'),
                            'api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Staging Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="staging-senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="staging-senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senpic.StagingSenbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senpicStagingSenbos'),
                            'staging-senbos-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="senshield">
                      <div className="flex flex-col gap-4 pt-2">
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="senshield-senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="senshield-senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senshield.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senshieldSenbos'),
                            'senshield-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="senforce">
                      <div className="flex flex-col gap-4 pt-2">
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="senforce-senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="senforce-senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senforce.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senforceSenbos'),
                            'senforce-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="alert-reviewer">
                      <div className="flex flex-col gap-5 pt-2">
                        <Controller
                          name="config.AlertReviewer.Enabled"
                          control={control}
                          render={({ field }) => {
                            // Unset = on, as in senhub.
                            const enabled = field.value !== false;
                            return (
                              <div
                                className={cn(
                                  'flex items-center justify-between gap-4 rounded-lg border p-4',
                                  !enabled &&
                                    'border-destructive/40 bg-destructive/5',
                                )}
                              >
                                <div className="grid gap-1">
                                  <Label htmlFor="alert-reviewer-enabled">
                                    Review alerts
                                  </Label>
                                  <p className="text-muted-foreground text-sm">
                                    {enabled
                                      ? 'New alerts are sent to the AI provider for review.'
                                      : 'Review is off: new alerts are not reviewed, manual re-reviews are refused and no AI calls are made.'}
                                  </p>
                                </div>
                                <Switch
                                  id="alert-reviewer-enabled"
                                  checked={enabled}
                                  onCheckedChange={field.onChange}
                                  disabled={readOnly}
                                />
                              </div>
                            );
                          }}
                        />

                        <Controller
                          name="config.AlertReviewer.UpdateAlertStatusEnabled"
                          control={control}
                          render={({ field }) => {
                            // Unset = off, as in senhub.
                            const enabled = field.value === true;
                            return (
                              <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                                <div className="grid gap-1">
                                  <Label htmlFor="alert-reviewer-update-alert-status">
                                    Update alert status
                                  </Label>
                                  <p className="text-muted-foreground text-sm">
                                    {enabled
                                      ? 'An AI rejection sets the alert to ai-review-rejected, hiding it from the Senbos review list. A reviewer approving it puts it back to detected. Alerts an officer has already actioned are never changed.'
                                      : 'AI decisions are stored on the alert only and do not change its status. Auto-accepted alert types still get ai-review-skipped.'}
                                  </p>
                                </div>
                                <Switch
                                  id="alert-reviewer-update-alert-status"
                                  checked={enabled}
                                  onCheckedChange={field.onChange}
                                  disabled={readOnly}
                                />
                              </div>
                            );
                          }}
                        />

                        <div className="grid gap-2">
                          <Label htmlFor="alert-reviewer-provider">
                            Active Provider
                          </Label>
                          <Controller
                            name="config.AlertReviewer.Provider"
                            control={control}
                            render={({ field }) => (
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={readOnly}
                              >
                                <SelectTrigger
                                  id="alert-reviewer-provider"
                                  className="w-full"
                                >
                                  <SelectValue placeholder="Select provider" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Claude">Claude</SelectItem>
                                  <SelectItem value="Gpt">Gpt</SelectItem>
                                  <SelectItem value="Gemini">Gemini</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="alert-reviewer-frame-types">
                            Frame Types
                          </Label>
                          <Controller
                            name="config.AlertReviewer.FrameTypes"
                            control={control}
                            render={({ field }) => (
                              <FrameTypesInput
                                value={field.value}
                                readOnly={readOnly}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </div>

                        <div className="border-green/40 bg-green/5 grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="green">GPT</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <div className="grid gap-2">
                              <Label>Model</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register('config.AlertReviewer.Gpt.Model')}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>API URL</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register('config.AlertReviewer.Gpt.ApiUrl')}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Max Tokens</Label>
                              <Input
                                type="number"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gpt.MaxTokens',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                            {renderTimeoutField('Gpt')}
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerGptApiKey'),
                            'alert-reviewer-gpt-api-key',
                            'API Key',
                          )}
                          <ProviderPromptFields
                            provider="Gpt"
                            register={register}
                            control={control}
                            readOnly={readOnly}
                          />
                        </div>

                        <div className="border-orange/40 bg-orange/5 grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="orange">Claude</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <div className="grid gap-2">
                              <Label>Model</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Claude.Model',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>API URL</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Claude.ApiUrl',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Max Tokens</Label>
                              <Input
                                type="number"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Claude.MaxTokens',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                            {renderTimeoutField('Claude')}
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerClaudeApiKey'),
                            'alert-reviewer-claude-api-key',
                            'API Key',
                          )}
                          <ProviderPromptFields
                            provider="Claude"
                            register={register}
                            control={control}
                            readOnly={readOnly}
                          />
                        </div>

                        <div className="border-blue/40 bg-blue/5 grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="blue">Gemini</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <div className="grid gap-2">
                              <Label>Model</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gemini.Model',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>API URL</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gemini.ApiUrl',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Max Tokens</Label>
                              <Input
                                type="number"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gemini.MaxTokens',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                            {renderTimeoutField('Gemini')}
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerGeminiApiKey'),
                            'alert-reviewer-gemini-api-key',
                            'API Key',
                          )}
                          <ProviderPromptFields
                            provider="Gemini"
                            register={register}
                            control={control}
                            readOnly={readOnly}
                          />
                        </div>

                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Hasura</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                              <Label htmlFor="alert-reviewer-hasura-url">
                                URL
                              </Label>
                              <Input
                                type="text"
                                id="alert-reviewer-hasura-url"
                                readOnly={readOnly}
                                {...register('config.AlertReviewer.Hasura.Url')}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label htmlFor="alert-reviewer-hasura-apikeyid">
                                API Key ID
                              </Label>
                              <Input
                                type="number"
                                id="alert-reviewer-hasura-apikeyid"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Hasura.ApiKeyId',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="alert-reviewer-senbos-url">
                              URL
                            </Label>
                            <Input
                              type="text"
                              id="alert-reviewer-senbos-url"
                              readOnly={readOnly}
                              {...register('config.AlertReviewer.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerSenbos'),
                            'alert-reviewer-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>

                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              Failure Notification
                            </Badge>
                            <Separator className="flex-1" />
                          </div>
                          <p className="text-muted-foreground text-sm">
                            Notifies Slack and/or email when this many different
                            alerts fail review in a row, and again when reviews
                            recover.
                          </p>
                          <div className="grid gap-2">
                            <Label htmlFor="alert-reviewer-failure-threshold">
                              Threshold
                            </Label>
                            <Input
                              type="number"
                              id="alert-reviewer-failure-threshold"
                              min={0}
                              step={1}
                              readOnly={readOnly}
                              placeholder="10 (default) - 0 turns notifications off"
                              {...register(
                                'config.AlertReviewer.FailureNotification.Threshold',
                                {
                                  // Empty = unset, so senhub's default applies.
                                  setValueAs: (v: unknown) =>
                                    v === '' || v === null || v === undefined
                                      ? undefined
                                      : Number(v),
                                  validate: (v) =>
                                    v === undefined ||
                                    (Number.isInteger(v) && v >= 0) ||
                                    'Threshold must be a whole number, 0 or more.',
                                },
                              )}
                            />
                            {failureThresholdError && (
                              <p className="text-destructive text-sm">
                                {failureThresholdError}
                              </p>
                            )}
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerSlackWebhook'),
                            'alert-reviewer-slack-webhook',
                            'Slack Webhook URL',
                          )}
                          <div className="grid gap-2">
                            <Label htmlFor="alert-reviewer-email-recipients">
                              Email Recipients
                            </Label>
                            <Controller
                              name="config.AlertReviewer.FailureNotification.EmailRecipients"
                              control={control}
                              rules={{
                                validate: (v) => {
                                  const recipients = v ?? [];
                                  const invalid = recipients.filter(
                                    (e) => !EMAIL_PATTERN.test(e),
                                  );
                                  if (invalid.length > 0) {
                                    return `Invalid email: ${invalid.join(', ')}`;
                                  }
                                  if (
                                    recipients.length > MAX_EMAIL_RECIPIENTS
                                  ) {
                                    return `At most ${MAX_EMAIL_RECIPIENTS} recipients.`;
                                  }
                                  return true;
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <>
                                  <CommaListInput
                                    id="alert-reviewer-email-recipients"
                                    value={field.value}
                                    readOnly={readOnly}
                                    placeholder="ops@sensen.ai, oncall@sensen.ai - empty = no email"
                                    onCommit={(text) =>
                                      field.onChange(parseCommaList(text))
                                    }
                                  />
                                  {fieldState.error && (
                                    <p className="text-destructive text-sm">
                                      {fieldState.error.message}
                                    </p>
                                  )}
                                </>
                              )}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerSmtp'),
                            'alert-reviewer-smtp-login',
                            'SMTP Login (sending mailbox)',
                          )}
                          {customerId && (
                            <TestNotificationButtons
                              customerId={customerId}
                              editing={!readOnly}
                              slackConfigured={getHasExistingSecret(
                                'alertReviewerSlackWebhook',
                              )}
                              emailConfigured={
                                (alertReviewerEmailRecipients?.length ?? 0) >
                                  0 && getHasExistingSecret('alertReviewerSmtp')
                              }
                            />
                          )}
                        </div>
                      </div>
                    </TabsContent>
                  </CardContent>
                </Card>
              </Tabs>
            </TabsContent>
            <TabsContent value="json">
              <Controller
                name="jsonConfig"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <p className="text-muted-foreground text-sm">
                      JSON mode is advanced and full-control. Secret reference
                      fields can be edited directly here.
                    </p>
                    <div className="h-[60vh] min-h-[400px]">
                      <JSONEditorFormItem
                        onValueChange={field.onChange}
                        value={field.value}
                        onSetError={() => {
                          setError('jsonConfig', {
                            message: 'Invalid JSON!',
                          });
                        }}
                        mode={
                          creatingCustomer ? 'text' : readOnly ? 'view' : 'text'
                        }
                        options={{
                          navigationBar: true,
                          mainMenuBar: true,
                        }}
                      />
                    </div>
                  </div>
                )}
              />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
      <Dialog open={!!secretEditorTarget} onOpenChange={closeSecretEditor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {secretEditorTarget
                ? `Edit ${secretEditorTarget.label}`
                : 'Edit Secret'}
            </DialogTitle>
            <DialogDescription>
              Stage username/password changes or mark this secret for deletion.
              Changes are applied when you save the customer in Form mode.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            {secretEditorTarget?.secretKind === 'credentials' && (
              <div className="grid gap-2">
                <Label htmlFor="secret-username">Username</Label>
                <Input
                  id="secret-username"
                  type="text"
                  value={secretEditorUsername}
                  onChange={(event) =>
                    setSecretEditorUsername(event.target.value)
                  }
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="secret-password">
                {secretEditorTarget?.secretKind === 'apiKey'
                  ? 'API Key'
                  : secretEditorTarget?.secretKind === 'url'
                    ? 'URL'
                    : 'Password'}
              </Label>
              <Input
                id="secret-password"
                type="password"
                value={secretEditorPassword}
                onChange={(event) =>
                  setSecretEditorPassword(event.target.value)
                }
              />
            </div>
            {secretEditorTarget?.id === 'alertReviewerSmtp' && (
              <p className="text-muted-foreground text-sm">
                The Google Workspace mailbox notifications are sent from, and an
                app password for it - not the account password.
              </p>
            )}
            {secretEditorError && (
              <p className="text-destructive text-sm">{secretEditorError}</p>
            )}
            {activeSecretDraft?.action === 'delete' && (
              <p className="text-muted-foreground text-sm">
                Secret deletion is currently staged.
              </p>
            )}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button
              type="button"
              variant="destructive"
              onClick={handleSecretDelete}
              disabled={!canDeleteSecret}
            >
              Delete Secret
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeSecretEditor}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSecretSave}>
                OK
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailsForm;
